import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import cn from 'classnames';
import { toast } from 'chameleon/ui-stack/ui/toast';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import BasicSettings, { FIELD_NAMES } from './BasicSettings';
import CasSetting from './CasSetting';
import AdvancedSettings, {
  FIELD_NAMES as ADVANCED_SETTINGS_FIELD_NAMES,
} from './AdvancedSettings';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import useFetchTeacherAssignments from 'chameleon/ui-stack/hooks/use-assignments-fetch/teacher';
import Footer from './Footer';
import useSppTelemetrySend from 'chameleon/ui-stack/utilities/use-spp-telemetry-send';
import { FIELD_META_MAP } from 'chameleon/src/components/assign/AssignManageForm/utils/constants';
import { validateStartAndDueDate } from 'legacy-src/utility/AssignModal';
import axios from 'axios';
import { generatePayload, showToastNotification } from './utils';
import { STATUS } from 'chameleon-src/constants/assessmentStatus';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import defaultTo from 'lodash/defaultTo';
import { QuestionTypes } from 'legacy/packages/unit-outline/src/constants';
import log from 'chameleon/ui-stack/utilities/log';
import {
  getClassDropdownDisabledStudents,
  getUnassignableSections,
} from './utils';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import InformationalPopover from 'chameleon/ui-stack/ui/popovers/informational-popover';
import DATA_TEST_IDS from 'chameleon/src/components/assign/AssignManageForm/utils/dataTestIds';
import DigitalAssessmentInfoModal from 'legacy/src/components/assessment/DigitalAssessmentInfoModal';
import { displayDigitalAssessmentInfoModal } from 'legacy/src/components/assessment/DigitalAssessmentInfoModal/utils';
import { v4 as uuidv4 } from 'uuid';
import Button from 'chameleon/ui-stack/ui/base/button';

const computerTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const Form = (props) => {
  const {
    assessment,
    assessmentWithQuestionsData,
    scrambleQuestionsPerStudent,
    isSPP,
    isPPC,
    isPreApSubject,
    onAssignModalClose,
    reviewModeFlag,
    subjectInfo,
    hasAudioRecordOrFileUpload,
    isPracticeExam,
    individualStudentAssignmentFlag,
    allowLateSubmissionFF,
    params,
    assessmentId,
    educationalPeriod,
    studentsBySection,
    router,
    isAssessmentForm,
    showNewAssignModalCasButton,
    onAssign,
    isAdminOrCoordinator,
    allowHardTimer,
    userPreferences,
    userSubjects,
    bluebookUIFF,
  } = props;

  const [isLoading, setLoading] = useState(true);
  const [assignments, setData] = useState(null);
  const [options, setOptions] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [digitalAssessmentInfoModalOpen, setDigitalAssessmentInfoModalOpen] =
    useState(false);

  const { subject } = params || {};
  const [getTeacherAssignments] = useFetchTeacherAssignments();
  const basicSettingsId = `basicSettings`;
  const advancedSettingsId = `advancedSettings`;

  useEffect(() => {
    const fetchProps = {
      subject,
      status: STATUS.ASSIGNED,
      setData,
      setLoading,
      setOptions,
      educationalPeriod,
      isLoading,
      assessmentId,
    };
    getTeacherAssignments({ ...fetchProps });
  }, []);

  const initialFormState = {
    basicSettings: {
      [FIELD_NAMES.CLASS_DROPDOWN]: '',
      [FIELD_NAMES.FORMAT]: FIELD_META_MAP.FORMAT.options[0].value,
      [FIELD_NAMES.STUDENT_RESULTS]:
        FIELD_META_MAP.STUDENT_RESULTS.options(reviewModeFlag)[0].value,
      [FIELD_NAMES.STARTS_AT]: dayjs(),
      [FIELD_NAMES.DUE_AT]: null,
      [FIELD_NAMES.TIMER]: null,
      [FIELD_NAMES.IS_SOFT_TIMER]: allowHardTimer ? false : true,
    },
    advancedSettings: {
      [ADVANCED_SETTINGS_FIELD_NAMES.STUDENT_SCORING]: false,
      [ADVANCED_SETTINGS_FIELD_NAMES.CUSTOM_ASSIGNMENT_NAME]: '',
      [ADVANCED_SETTINGS_FIELD_NAMES.SCRAMBLE_QUESTIONS]: false,
      [ADVANCED_SETTINGS_FIELD_NAMES.LOCKDOWN_BROWSER]: false,
      [ADVANCED_SETTINGS_FIELD_NAMES.IMMEDIATE_FEEDBACK]: false,
      [ADVANCED_SETTINGS_FIELD_NAMES.IMMEDIATE_FEEDBACK_ATTEMPTS]: '0',
    },
  };

  const [casSettings, setCasSettings] = useState([]);

  // Initialize telemetry if applicable
  const sppTelemetrySend =
    isSPP &&
    useSppTelemetrySend({
      subject: subjectInfo,
    });

  const formMethods = useForm({
    defaultValues: {
      basicSettings: initialFormState.basicSettings,
      advancedSettings: initialFormState.advancedSettings,
    },
  });
  const {
    getValues,
    setValue,
    handleSubmit,
    setError,
    watch,
    clearErrors,
    control,
  } = formMethods || {};

  const addSettings = () => {
    const newId = `cas-${uuidv4()}`;
    setValue(`${newId}.${FIELD_NAMES.CLASS_DROPDOWN}`, {});
    setValue(
      `${newId}.${FIELD_NAMES.STARTS_AT}`,
      getValues(`${basicSettingsId}.${FIELD_NAMES.STARTS_AT}`),
    );
    setValue(
      `${newId}.${FIELD_NAMES.DUE_AT}`,
      getValues(`${basicSettingsId}.${FIELD_NAMES.DUE_AT}`),
    );
    setValue(
      `${newId}.${FIELD_NAMES.TIMER}`,
      getValues(`${basicSettingsId}.${FIELD_NAMES.TIMER}`),
    );

    const newSetting = {
      id: newId,
      settingNumber: casSettings.length + 2,
    };
    setCasSettings([...casSettings, newSetting]);
  };

  const deleteCasSetting = ({ settingsId }) => {
    const newCasSettings = casSettings
      .filter(({ id }) => id !== settingsId)
      .reduce(
        (acc, current, index) => [
          ...acc,
          {
            ...current,
            settingNumber: index + 2,
          },
        ],
        [],
      );
    setCasSettings(newCasSettings);
  };

  const timezone = defaultTo(get(options, 'groups[0].timezone'), computerTZ);

  // Handle form submission
  const onSubmit = handleSubmit(async (formData) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const { basicSettings, advancedSettings } = formData;
    /**
     * Do date validation on all form sections but advanced settings
     * (that doesn't have dates) before submitting
     */
    const hasErrors = Object.keys(omit(formData, ['advancedSettings']))
      .map((settingId) => {
        const settings = formData[settingId];
        const hasValidDates = validateStartAndDueDate(
          settings[FIELD_NAMES.STARTS_AT],
          settings[FIELD_NAMES.DUE_AT],
          (field, message) => {
            setError(`${settingId}.${field}`, {
              type: 'custom',
              message,
            });
          },
          (field) => {
            clearErrors(`${settingId}.${field}`);
          },
          FIELD_NAMES.STARTS_AT,
          FIELD_NAMES.DUE_AT,
        );
        return hasValidDates;
      })
      .some((validationTest) => validationTest == false);
    if (hasErrors) {
      setIsSubmitting(false);
      return;
    }

    // TODO: handle the 'data' submission here
    // Send telemetry data if SPP is enabled
    if (isSPP) {
      const telemetryData = {
        //   assigned: isAssign ? 1 : 0,
        //   assigned_date: new Date(),
        //   assigned_start: formData.startsAt && formData.startsAt.toDate(),
        //   assigned_due: formData.dueAt && formData.dueAt.toDate(),
        //   spp_id: itemId,
        //   spp_learnosity_reference:
        //     learnosityReference ||
        //     get(assignment, 'assessment.learnosity_reference'),
        //   spp_title: itemTitle || get(assignment, 'title'),
        //   event_type: isAssign
        //     ? TELEMETRY_EVENTS.teacherAssign
        //     : TELEMETRY_EVENTS.teacherUpdate,
        //   assigned_from_spp_player: eventFromSPPPlayer,
      };
      // if (!isAssign) {
      //   telemetryData.assignment_id = assignment.id;
      sppTelemetrySend && sppTelemetrySend(telemetryData);
    }

    const activeCasSettings = Array.from(
      casSettings,
      (casSetting) => formData[casSetting?.id],
    );
    const payload = generatePayload(
      basicSettings,
      advancedSettings,
      activeCasSettings,
      assessmentWithQuestionsData.id,
      isSPP,
      subjectInfo,
      timezone,
      allowHardTimer,
    );

    try {
      const apiURL = `${FYM_BACKEND_URL}/assessments/api/v2/assignments/`;
      const { data } = await axios.post(apiURL, payload);

      showToastNotification({
        subjectInfo,
        assignmentsResponse: data[0],
        studentsBySection,
      });

      onAssign(data[0]);
    } catch (error) {
      log('error', error);
      toast.error('An error occurred during assign.');
    } finally {
      setIsSubmitting(false);
      onAssignModalClose();
    }
  });

  const isPrint = watch(`${basicSettingsId}.${FIELD_NAMES.FORMAT}`);

  const submitForm = () => {
    if (shouldDisplayDigitalAssessmentInfoModal) {
      setDigitalAssessmentInfoModalOpen(true);
    } else {
      onSubmit();
    }
  };

  // Array of for all casSettings
  const casSettingsIds = casSettings.map((setting) => setting.id);

  // Watch for class dropdown changes
  const classDropdownValues = useWatch({
    control,
    name: [
      `${basicSettingsId}.${FIELD_NAMES.CLASS_DROPDOWN}`,
      ...casSettingsIds.map((id) => `${id}.${FIELD_NAMES.CLASS_DROPDOWN}`),
    ],
  });

  /**
   * For print assignments, only the main settings matter and the other
   * active CAS settings should be ignored when calculating whether we
   * have at least one class/student selected.
   */
  const classOrStudentSelected = isPrint
    ? Object.keys(get(classDropdownValues, [0], {}) || {}).length > 0
    : classDropdownValues.some(
        (setting) => Object.keys(setting || {}).length > 0,
      );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const assignmentList = get(assignments, '[0].assignments', []) || [];
  const disabledStudents = getClassDropdownDisabledStudents(assignmentList);
  const unassignableSections = getUnassignableSections(
    assignmentList,
    disabledStudents,
    studentsBySection,
  );

  const assignableSections = subjectInfo?.classes?.filter(
    (cls) =>
      cls.educationPeriod === parseInt(educationalPeriod, 10) &&
      !unassignableSections.includes(Number.parseInt(cls.id, 10)),
  );

  const isMCQPPC =
    isPPC &&
    get(assessmentWithQuestionsData, 'questions[0].type', '') ===
      QuestionTypes.MULTIPLE_CHOICE_QUESTION;

  const hasMcqQuestion = assessmentWithQuestionsData?.questions?.some(
    (question) => question.type === QuestionTypes.MULTIPLE_CHOICE_QUESTION,
  );

  const hasFrqQuestions = assessmentWithQuestionsData?.questions?.some(
    (question) => question?.type === QuestionTypes.FREE_RESPONSE_QUESTION,
  );

  const isLC = assessment?.is_learning_checkpoint || false;

  const withAdvancedSettings = !(isPreApSubject || isSPP);
  let latestSettingId = basicSettingsId;
  if (casSettings.length > 0) {
    latestSettingId = casSettings[casSettings.length - 1].id;
  }
  const latestSelectedAssignees = watch(
    `${latestSettingId}.${FIELD_NAMES.CLASS_DROPDOWN}`,
  );
  const noAssigneesSelected =
    isPrint || isEmpty(Object.values(latestSelectedAssignees).flat());
  const infoPopupText =
    '\
      Apply a different start date, due date, or timer to specific\
      students within this assignment, or to students in another class.';

  const infoPopupLearnMore =
    'https://apclassroom.clickhelp.co/articles/ap-classroom-user-guide-for-teachers-publication/tch-assigning-assessments';

  const isAssignActive =
    !isAdminOrCoordinator &&
    assignableSections.length > 0 &&
    classOrStudentSelected &&
    !isSubmitting;

  const { type, is_in_co_taught_class: isCoTaught } = assessment;
  const shouldDisplayDigitalAssessmentInfoModal =
    displayDigitalAssessmentInfoModal({
      subject: router.params.subject,
      bluebookUIFF,
      userPreferences,
      userSubjects,
      assesmentType: type,
      isCoTaught,
    });
  const casButtonText = 'Apply Different Settings to Classes or Students';

  return (
    <div className="flex flex-col max-h-screen">
      {isAdminOrCoordinator && (
        <div
          data-test-id="coordinator-instruction"
          className="px-16 py-6 bg-blue-600 bg-opacity-10 w-full"
        >
          Teachers select 1 or more classes and any of the following options to
          assign assessments.
        </div>
      )}
      <FormProvider {...formMethods}>
        <div className="border rounded-md mx-6 p-6 bg-gray-200 mb-8">
          <BasicSettings
            key={basicSettingsId}
            id={basicSettingsId}
            scrambleQuestionsPerStudent={scrambleQuestionsPerStudent}
            isAdminOrCoordinator={isAdminOrCoordinator}
            isSPP={isSPP}
            isPreApSubject={isPreApSubject}
            reviewModeFlag={reviewModeFlag}
            individualStudentAssignmentFlag={individualStudentAssignmentFlag}
            isMCQPPC={isMCQPPC}
            allowLateSubmissionFF={allowLateSubmissionFF}
            assignments={get(assignments, '[0].assignments', []) || []}
            assignableSections={assignableSections}
            disabledStudents={disabledStudents}
            studentsBySection={studentsBySection}
            isAssessmentForm={isAssessmentForm}
            router={router}
            timezone={timezone}
            casSettingsCount={casSettings.length}
            otherActiveSettings={casSettings}
            allowHardTimer={allowHardTimer}
            isLC={isLC}
          />
          {withAdvancedSettings && (
            <AdvancedSettings
              key={advancedSettingsId}
              id={advancedSettingsId}
              basicSettingsId={basicSettingsId}
              isMCQPPC={isMCQPPC}
              isPPC={isPPC}
              isPrint={isPrint}
              hasFrqQuestions={hasFrqQuestions}
              hasMcqQuestions={hasMcqQuestion}
              hasAudioRecordOrFileUpload={hasAudioRecordOrFileUpload}
              isPracticeExam={isPracticeExam}
            />
          )}
        </div>
        {!isPrint &&
          casSettings.map((setting) => {
            return (
              <div className="border rounded-md mx-6 p-6 bg-gray-200 mb-8">
                <CasSetting
                  key={setting.id}
                  id={setting.id}
                  scrambleQuestionsPerStudent={scrambleQuestionsPerStudent}
                  isSPP={isSPP}
                  isPreApSubject={isPreApSubject}
                  reviewModeFlag={reviewModeFlag}
                  individualStudentAssignmentFlag={
                    individualStudentAssignmentFlag
                  }
                  isMCQPPC={isMCQPPC}
                  assignments={get(assignments, '[0].assignments', []) || []}
                  studentsBySection={studentsBySection}
                  router={router}
                  isAssessmentForm={isAssessmentForm}
                  settingNumber={setting.settingNumber}
                  otherActiveSettings={[
                    { id: basicSettingsId, settingNumber: 1 },
                    ...casSettings.filter(({ id }) => id !== setting.id),
                  ]}
                  deleteCasSetting={deleteCasSetting}
                  assignableSections={assignableSections}
                  disabledStudents={disabledStudents}
                  timezone={timezone}
                  allowLateSubmissionFF={allowLateSubmissionFF}
                  allowHardTimer={allowHardTimer}
                />
              </div>
            );
          })}
        {showNewAssignModalCasButton && (
          <div className="relative mx-6 border rounded-md bg-gray-200">
            <div className="flex items-center h-[5.6rem]">
              <ButtonWithIcon
                className="pl-6 add-different-custom-setitngs flex items-center bg-gray-200 h-full"
                icon="add-circle"
                iconWidth={24}
                iconHeight={24}
                onClick={addSettings}
                size="custom"
                disabled={noAssigneesSelected}
                dataTestId={DATA_TEST_IDS.ADD_CAS_SETTINGS_BUTTON}
                accessibility={{
                  'aria-label': casButtonText,
                }}
                iconAccessibility={{
                  ariaHidden: true,
                }}
              >
                <span
                  className={cn('text-16 font-medium', {
                    'text-gray-500': noAssigneesSelected,
                    'text-gray-900': !noAssigneesSelected,
                  })}
                >
                  {casButtonText}
                </span>
              </ButtonWithIcon>
              <div className="ml-4">
                <InformationalPopover
                  data-test-id={DATA_TEST_IDS.CAS_SETTINGS_INFO_POPOVER}
                  size="medium"
                  defaultOpen={false}
                  iconWidth={15}
                  iconHeight={15}
                  iconTextColor={
                    noAssigneesSelected ? 'text-gray-500' : 'text-gray-600'
                  }
                  disabled={noAssigneesSelected}
                  withPortal
                  tabIndex={noAssigneesSelected ? -1 : 0}
                >
                  <span
                    aria-live="assertive"
                    aria-label={infoPopupText}
                    className="text-base leading-8"
                  >
                    {infoPopupText}&nbsp;
                    <a
                      href={infoPopupLearnMore}
                      target="_blank"
                      aria-label="Click to Learn More about Assignment Custom Settings"
                    >
                      Learn More.
                    </a>
                  </span>
                </InformationalPopover>
              </div>
              <Button
                className={`w-full h-full bg-gray-200 ${noAssigneesSelected ? 'cursor-default' : 'cursor-pointer'}`}
                tabIndex="-1"
                onClick={addSettings}
                aria-hidden="true"
                disabled={noAssigneesSelected}
              />
            </div>
          </div>
        )}
        <Footer
          onSubmit={submitForm}
          canSubmit={isAssignActive}
          onClose={onAssignModalClose}
          submitBtnText="Assign"
          closeBtnText="Cancel"
        />
      </FormProvider>
      {shouldDisplayDigitalAssessmentInfoModal &&
        digitalAssessmentInfoModalOpen && (
          <DigitalAssessmentInfoModal
            onClose={() => setDigitalAssessmentInfoModalOpen(false)}
            onSubmit={onSubmit}
          />
        )}
    </div>
  );
};

export default Form;
